import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronLeft, Search, X, ShieldCheck, User, Clock, Save } from 'lucide-react'
import { useAdminUsers, useAdminUpdateUser, type AdminUser } from '@/hooks/useAdmin'
import { PillTabs } from '@/components/PillTabs'
import { EmptyState } from '@/components/EmptyState'
import { SkeletonList } from '@/components/LoadingSkeleton'
import { ErrorState } from '@/components/ErrorState'
import { cn } from '@/lib/utils'

/** Sprint "Corrección Final Product Experience V2" (10/09/2026, Secciones 7/8/9/10 del
 * encargo): "Administración → Usuarios" -- SOLO visible/accesible para ADMIN (gateado por
 * `MorePage.tsx` a nivel de navegación Y por `require_admin_role` en el backend, la barrera
 * real). Reutiliza íntegramente `/admin/users` + `PATCH /admin/users/{id}` -- nunca una
 * segunda arquitectura administrativa, nunca DELETE físico (Sección 9: "conservar
 * historial/apuestas/pagos/analytics/trazabilidad"). */

const STATUS_LABEL: Record<string, string> = {
  pending: 'Pendiente', active: 'Activo', suspended: 'Suspendido', expired: 'Vencido', blocked: 'Bloqueado',
}
const STATUS_COLOR: Record<string, string> = {
  pending: 'text-warn border-warn/40 bg-warn-soft/10',
  active: 'text-brand border-brand/40 bg-brand-soft/10',
  suspended: 'text-danger border-danger/40 bg-danger-soft/10',
  expired: 'text-text-dim border-border bg-panel-elevated',
  blocked: 'text-danger border-danger/40 bg-danger-soft/20',
}
const PLAN_OPTIONS = ['FREE', 'PRO', 'BLACK']

function fmtDate(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })
}
function fmtDateTime(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('es-ES', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

function UserRow({ user, onOpen }: { user: AdminUser; onOpen: () => void }) {
  return (
    <button onClick={onOpen} className="data-row w-full text-left hover:bg-panel-elevated/30 -mx-1 px-1 rounded-lg transition-colors">
      <div className="min-w-0 flex-1">
        <p className="text-xs font-bold text-text truncate">{user.display_name || user.telegram_username || `ID ${user.telegram_user_id}`}</p>
        <p className="text-[10px] text-text-dim truncate">
          {user.telegram_username ? `@${user.telegram_username}` : `tg:${user.telegram_user_id}`} · Alta {fmtDate(user.created_at)}
        </p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span className="text-[9px] font-black uppercase tracking-wider text-text-dim border border-border rounded-full px-2 py-0.5">{user.plan}</span>
        <span className={cn('text-[9px] font-black uppercase tracking-wider border rounded-full px-2 py-0.5', STATUS_COLOR[user.status])}>
          {STATUS_LABEL[user.status] ?? user.status}
        </span>
      </div>
    </button>
  )
}

function UserDetailSheet({ user, onClose }: { user: AdminUser; onClose: () => void }) {
  const [plan, setPlan] = useState(user.plan)
  const [status, setStatus] = useState(user.status)
  const [expiresAt, setExpiresAt] = useState(user.expires_at ? user.expires_at.slice(0, 10) : '')
  const [notes, setNotes] = useState(user.notes ?? '')
  const update = useAdminUpdateUser()

  const dirty = plan !== user.plan || status !== user.status || notes !== (user.notes ?? '') ||
    expiresAt !== (user.expires_at ? user.expires_at.slice(0, 10) : '')

  function save() {
    update.mutate({
      userId: user.id,
      plan: plan !== user.plan ? plan : undefined,
      status: status !== user.status ? status : undefined,
      expires_at: expiresAt !== (user.expires_at ? user.expires_at.slice(0, 10) : '')
        ? (expiresAt ? new Date(`${expiresAt}T23:59:59`).toISOString() : undefined)
        : undefined,
      notes: notes !== (user.notes ?? '') ? notes : undefined,
    }, { onSuccess: onClose })
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-panel rounded-t-3xl border-t border-border max-h-[88vh] overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-panel border-b border-border px-5 py-3.5 flex items-center justify-between z-10">
          <div className="flex items-center gap-2">
            <User className="h-4 w-4 text-brand" />
            <p className="text-xs font-black uppercase tracking-wider text-text">Detalle de usuario</p>
          </div>
          <button onClick={onClose} aria-label="Cerrar" className="p-1.5 rounded-xl bg-panel-elevated text-text-dim hover:text-text">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Identidad */}
          <div className="bg-panel-elevated/40 border border-border/40 p-4 rounded-2xl space-y-1.5">
            <p className="text-sm font-black text-text">{user.display_name || 'Sin nombre'}</p>
            <p className="text-xs text-text-dim">
              {user.telegram_username ? `@${user.telegram_username}` : 'Sin username'} · Telegram ID <span className="figure">{user.telegram_user_id}</span>
            </p>
            {user.role === 'ADMIN' && (
              <span className="inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-wider text-exact"><ShieldCheck className="h-3 w-3" /> Rol ADMIN</span>
            )}
          </div>

          {/* Fechas */}
          <div className="grid grid-cols-2 gap-2.5">
            <div className="bg-panel-elevated/50 p-3 rounded-xl border border-border/40">
              <p className="text-[9px] font-bold uppercase tracking-wider text-text-dim">Fecha de alta</p>
              <p className="text-xs font-bold text-text mt-0.5">{fmtDate(user.created_at)}</p>
            </div>
            <div className="bg-panel-elevated/50 p-3 rounded-xl border border-border/40">
              <p className="text-[9px] font-bold uppercase tracking-wider text-text-dim flex items-center gap-1"><Clock className="h-2.5 w-2.5" /> Último acceso</p>
              <p className="text-xs font-bold text-text mt-0.5">{fmtDateTime(user.last_access_at)}</p>
            </div>
          </div>

          {/* Plan */}
          <div className="space-y-2">
            <p className="eyebrow">Plan</p>
            <div className="grid grid-cols-3 gap-2">
              {PLAN_OPTIONS.map((p) => (
                <button
                  key={p}
                  onClick={() => setPlan(p)}
                  className={cn(
                    'py-2 rounded-xl text-xs font-black uppercase tracking-wider border transition',
                    plan === p ? 'bg-brand text-black border-brand' : 'bg-panel-elevated text-text-dim border-border hover:text-text',
                  )}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>

          {/* Estado */}
          <div className="space-y-2">
            <p className="eyebrow">Estado</p>
            <div className="grid grid-cols-2 gap-2">
              {(['active', 'suspended', 'blocked', 'pending'] as const).map((s) => (
                <button
                  key={s}
                  onClick={() => setStatus(s)}
                  className={cn(
                    'py-2 rounded-xl text-xs font-bold border transition',
                    status === s ? STATUS_COLOR[s] + ' border-current' : 'bg-panel-elevated text-text-dim border-border hover:text-text',
                  )}
                >
                  {STATUS_LABEL[s]}
                </button>
              ))}
            </div>
          </div>

          {/* Vencimiento */}
          <div className="space-y-2">
            <p className="eyebrow">Vencimiento</p>
            <input
              type="date"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              className="w-full bg-panel-elevated border border-border rounded-xl px-3 py-2.5 text-sm font-mono font-bold text-text focus:outline-none focus:border-brand/50"
            />
            <p className="text-[10px] text-text-dim">Vacío = sin vencimiento.</p>
          </div>

          {/* Notas */}
          <div className="space-y-2">
            <p className="eyebrow">Notas internas</p>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full bg-panel-elevated border border-border rounded-xl px-3 py-2.5 text-xs text-text focus:outline-none focus:border-brand/50 resize-none"
              placeholder="Sin notas"
            />
          </div>

          {update.isError && (
            <p className="text-xs text-danger">No se pudo guardar. Intenta de nuevo.</p>
          )}

          <button
            onClick={save}
            disabled={!dirty || update.isPending}
            className="btn-brand-app w-full justify-center text-xs font-bold disabled:opacity-40 disabled:pointer-events-none"
          >
            <Save className="h-3.5 w-3.5" /> {update.isPending ? 'Guardando…' : 'Guardar cambios'}
          </button>
        </div>
      </div>
    </div>
  )
}

type StatusFilter = 'all' | 'active' | 'inactive'

export function AdminUsersPage() {
  const { data, isLoading, isError, error, refetch } = useAdminUsers(true)
  const [query, setQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [planFilter, setPlanFilter] = useState<string | null>(null)
  const [selected, setSelected] = useState<AdminUser | null>(null)

  const users = data?.users ?? []
  const plansPresent = useMemo(() => Array.from(new Set(users.map((u) => u.plan))).sort(), [users])

  const filtered = users.filter((u) => {
    if (statusFilter === 'active' && u.status !== 'active') return false
    if (statusFilter === 'inactive' && u.status === 'active') return false
    if (planFilter && u.plan !== planFilter) return false
    if (query.trim()) {
      const q = query.trim().toLowerCase()
      const haystack = `${u.display_name ?? ''} ${u.telegram_username ?? ''} ${u.telegram_user_id}`.toLowerCase()
      if (!haystack.includes(q)) return false
    }
    return true
  })

  return (
    <div className="p-4 space-y-4 md:max-w-2xl md:mx-auto">
      <Link to="/mas" className="flex items-center gap-1 text-xs font-bold text-text-dim w-fit">
        <ChevronLeft className="h-4 w-4" /> Más
      </Link>
      <div>
        <h1 className="text-lg font-black text-text">Usuarios</h1>
        <p className="text-xs text-text-dim">{users.length} usuario{users.length === 1 ? '' : 's'} registrados</p>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-text-dim" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar por nombre, username o Telegram ID…"
          className="w-full bg-panel border border-border rounded-xl py-2.5 pl-9 pr-3 text-xs font-bold text-text focus:outline-none focus:border-brand/50"
        />
      </div>

      <PillTabs
        items={[
          { key: 'all', label: 'Todos' },
          { key: 'active', label: 'Activos' },
          { key: 'inactive', label: 'Inactivos' },
        ]}
        active={statusFilter}
        onChange={(k) => setStatusFilter(k as StatusFilter)}
      />
      {plansPresent.length > 1 && (
        <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar">
          <button
            onClick={() => setPlanFilter(null)}
            className={cn('shrink-0 px-2.5 py-1 rounded-lg text-[10px] font-bold', planFilter === null ? 'text-brand' : 'text-text-dim')}
          >
            Todos los planes
          </button>
          {plansPresent.map((p) => (
            <button
              key={p}
              onClick={() => setPlanFilter(p)}
              className={cn('shrink-0 px-2.5 py-1 rounded-lg text-[10px] font-bold border', planFilter === p ? 'border-brand text-brand' : 'border-border text-text-dim')}
            >
              {p}
            </button>
          ))}
        </div>
      )}

      {isLoading && <SkeletonList count={4} />}
      {isError && <ErrorState error={error} onRetry={() => refetch()} />}

      {!isLoading && !isError && filtered.length === 0 && (
        <EmptyState icon={User} title="Sin usuarios" body="Ningún usuario coincide con este filtro." />
      )}

      {filtered.length > 0 && (
        <div className="card p-3 divide-y divide-border/50">
          {filtered.map((u) => <UserRow key={u.id} user={u} onOpen={() => setSelected(u)} />)}
        </div>
      )}

      {selected && <UserDetailSheet user={selected} onClose={() => setSelected(null)} />}
    </div>
  )
}
